<template>
  <div class="page-wrap">
    <div v-if="loaded">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="page-header__left">
          <button class="btn-back" @click="$router.back()">
            <i class="fa fa-arrow-left"></i>
          </button>
          <div>
            <h1 class="page-title">Cart #{{ cart.id }}</h1>
            <div class="page-meta">
              <span class="meta-item">
                <i class="fa fa-calendar"></i>
                {{ formatDate(cart.last_update) }}
              </span>
              <span class="meta-item">
                <i class="fa fa-user"></i>
                {{ cart.meta.buyer_email || '—' }}
                <span class="buyer-type-pill">{{ cart.meta.buyer_type || '—' }}</span>
              </span>
            </div>
          </div>
        </div>
        <div class="page-header__right">
          <span v-html="getStatusBadge"></span>
        </div>
      </div>

      <!-- ── Customer Info ── -->
      <div class="info-grid">
        <div class="info-card info-card--link" @click="$router.push(`/users/${cart.user.id}`)">
          <div class="info-card__label">Customer ID</div>
          <div class="info-card__value link-cell">#{{ cart.user.id }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Customer Name</div>
          <div class="info-card__value">{{ cart.user.first_name }} {{ cart.user.last_name }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Email</div>
          <div class="info-card__value">{{ cart.user.email || '—' }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Phone</div>
          <div class="info-card__value">{{ cart.user.phone_number || '—' }}</div>
        </div>
        <div class="info-card info-card--link" v-if="cart.meta.invoice_id" @click="$router.push(`/invoices/${cart.meta.invoice_id}`)">
          <div class="info-card__label">Invoice Code</div>
          <div class="info-card__value link-cell">DD00{{ cart.meta.invoice_code }}</div>
        </div>
        <div class="info-card" v-if="cart.meta.payment_method">
          <div class="info-card__label">Payment Method</div>
          <div class="info-card__value">
            {{ Array.isArray(cart.meta.payment_method) ? cart.meta.payment_method.join(', ') : cart.meta.payment_method }}
            <div class="ref-id" v-if="cart.meta.reference">
              {{ cart.meta.reference.tap_id || cart.meta.reference.guid }}
            </div>
          </div>
        </div>
      </div>

      <!-- ── Items Table ── -->
      <div class="table-panel">
        <div class="table-panel__header">
          <div class="panel-title"><i class="fa fa-shopping-cart"></i> Cart Items</div>
        </div>
        <div class="table-responsive-wrap">
          <table class="dd-table">
            <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Title</th>
              <th>Additional Services</th>
              <th>Passengers</th>
              <th>Order / Gift</th>
              <th>Status</th>
              <th>Subtotal</th>
              <th v-if="!isClosed">Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="(item, index) in cart.items"
                :key="item.id"
                :class="['dd-table__row', { 'dd-table__row--refunded': item.meta.is_refunded }]"
            >
              <td><span class="order-id">#{{ item.id }}</span></td>
              <td>
                <span class="type-badge">{{ getItemType(item) }}</span>
              </td>
              <td>
                <span class="item-title">{{ item.meta.title }}</span>
                <span v-if="item.options.pay_later" class="pay-later-badge" title="Pay Later">🏠 Pay Later</span>
              </td>
              <td>
                <div v-if="Array.isArray(item.meta.additional_services) && item.meta.additional_services.length" class="additional-services">
                  <div v-for="(service, serviceIndex) in item.meta.additional_services" :key="serviceIndex" class="additional-services__line">
                    {{ service.title }} — {{ service.quantity }} × {{ service.amount }} {{ service.currency }}
                  </div>
                  <div v-if="item.meta.additional_services_total != null" class="additional-services__total">
                    Total: {{ item.meta.additional_services_total }} AED
                  </div>
                </div>
                <span v-else class="text-muted">—</span>
              </td>
              <td v-html="getPassengersForHuman(item)"></td>
              <td>
                  <span
                      class="link-cell"
                      v-if="item.meta.order_id"
                      @click.prevent="$router.push(`/orders/${item.meta.order_id}`)"
                  >#{{ item.meta.order_id }}</span>
                <span
                    class="link-cell"
                    v-if="item.meta.gift_id"
                    @click.prevent="$router.push(`/gifts/${item.meta.gift_id}`)"
                >#{{ item.meta.gift_id }}</span>
                <span class="text-muted" v-if="!item.meta.order_id && !item.meta.gift_id">—</span>
              </td>
              <td>
                <span class="gift-status" v-if="item.meta.gift_status">{{ item.meta.gift_status }}</span>
                <span class="text-muted" v-else>—</span>
              </td>
              <td>
                <span class="subtotal">{{ item.meta.sub_total }} AED</span>
              </td>
              <td v-if="!isClosed">
                <div class="action-btns">
                  <button
                      class="btn-icon btn-icon--green"
                      title="Copy item"
                      @click.prevent="showCopyModal(item.id)"
                      v-if="item.type === 'gift' && itemIsNotCopied(index)"
                  >
                    <i class="fa fa-copy"></i>
                  </button>
                  <button
                      class="btn-icon btn-icon--red"
                      title="Delete item"
                      @click.prevent="deleteItem(item.id)"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Summary + Actions ── -->
      <div class="bottom-grid">

        <!-- Summary -->
        <div class="panel summary-panel">
          <div class="panel-title"><i class="fa fa-calculator"></i> Price Summary</div>
          <div class="summary-rows">
            <div class="summary-row">
              <span>Number of Items</span>
              <span class="summary-val">{{ cart.items.length }}</span>
            </div>
            <div class="summary-row" v-if="cart.meta['delivery_cost']">
              <span>Delivery Cost</span>
              <span class="summary-val">{{ cart.meta['delivery_cost'] }} AED</span>
            </div>
            <div class="summary-row">
              <span>Additional Cost</span>
              <span class="summary-val">0.00 AED</span>
            </div>
            <div class="summary-row">
              <span>Promotion Discount</span>
              <span class="summary-val text-accent">{{ cart.promotion_amount }} AED</span>
            </div>
            <div class="summary-row">
              <span>Total Price</span>
              <span class="summary-val">{{ cart.meta['total_price'] }} AED</span>
            </div>
            <div class="summary-row" v-if="cart.meta['vat']">
              <span>VAT (5%)</span>
              <span class="summary-val">{{ cart.meta['vat'] }} AED</span>
            </div>
            <div class="summary-row">
              <span>Discount</span>
              <span class="summary-val">0.00 AED</span>
            </div>
            <div class="summary-row">
              <span>Transaction Amount</span>
              <span class="summary-val">0.00 AED</span>
            </div>
            <div class="summary-row summary-row--total" v-if="cart.meta['payable']">
              <span>Payable</span>
              <span class="summary-val summary-val--green">{{ cart.meta['payable'] }} AED</span>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="panel actions-panel" v-if="!isClosed">
          <div class="panel-title"><i class="fa fa-bolt"></i> Actions</div>
          <div class="actions-list">
            <button class="action-btn action-btn--primary" @click.prevent="$modal.show('pending-modal')">
              <i class="fa fa-clock-o"></i>
              {{ isPending ? 'Add Comment' : 'Change to In Process' }}
            </button>
            <button class="action-btn action-btn--secondary" v-if="isPending" @click="changeToOpen">
              <i class="fa fa-undo"></i>
              Change to Open
            </button>
            <button class="action-btn action-btn--green" @click.prevent="$modal.show('confirm-modal')">
              <i class="fa fa-check"></i>
              Confirm Cart
            </button>
            <button class="action-btn action-btn--amber" @click.prevent="$modal.show('logs-modal')">
              <i class="fa fa-history"></i>
              View Logs
            </button>
          </div>
        </div>

      </div>

    </div>

    <!-- Loading -->
    <div class="mt-5 pt-5">
      <loading v-if="!loaded"></loading>
    </div>

    <!-- ── Modals ── -->
    <modal name="copy-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Set Item Quantity</span>
          <button class="dd-modal__close" @click="$modal.hide('copy-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <label class="filter-label">Quantity</label>
          <input v-model="cpNewQuantity" type="number" min="1" max="100" class="dd-input" />
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('copy-modal')">Cancel</button>
          <button class="btn-submit" @click="copyItem">
            <i class="fa fa-check"></i> Submit
          </button>
        </div>
      </div>
    </modal>

    <modal name="confirm-modal" height="auto" v-if="loaded">
      <confirm-modal :cart="cart" @done="loadData()"></confirm-modal>
    </modal>

    <modal name="pending-modal" height="auto" v-if="loaded">
      <pending-modal :cart="cart" @done="loadData()"></pending-modal>
    </modal>

    <modal name="logs-modal" height="auto" v-if="loaded">
      <logs-modal :id="cart.id"></logs-modal>
    </modal>

  </div>
</template>

<script>
import ConfirmModal from "../components/ViewComponents/Carts/ConfirmModal.vue";
import PendingModal from "../components/ViewComponents/Carts/PendingModal.vue";
import CartLogsModal from "../components/ViewComponents/Carts/Logs.vue";
import { config } from "../helpers";

export default {
  name: "Cart",
  components: {
    "confirm-modal": ConfirmModal,
    "pending-modal": PendingModal,
    "logs-modal":    CartLogsModal,
  },
  data() {
    return {
      id: this.$route.params.id,
      loaded: false,
      cart: null,
      cpItemId: null,
      cpNewQuantity: null,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/carts";
    this.loadData();
  },

  methods: {
    loadData() {
      axios.get(`${this.url}/${this.id}`).then((r) => {
        this.cart   = r.data.data;
        this.loaded = true;
      });
    },

    showCopyModal(itemId) {
      this.cpItemId      = itemId;
      this.cpNewQuantity = 1;
      this.$modal.show("copy-modal");
    },

    copyItem() {
      if (this.cpNewQuantity == 1) return this.$modal.hide("copy-modal");
      this.loaded = false;
      axios
          .post(`${this.url}/${this.id}/items/${this.cpItemId}/copy`, { copies: this.cpNewQuantity - 1 })
          .then(() => { this.$modal.hide("copy-modal"); this.loadData(); })
          .catch((e) => { alert(Object.values(e.response.data.errors).flat()[0] ?? e.response.data?.meta?.message); });
    },

    deleteItem(itemId) {
      if (!confirm("Are you sure about deleting this item?")) return;
      this.loaded = false;
      axios
          .delete(`${this.url}/${this.id}/items/${itemId}`)
          .then(() => this.loadData())
          .catch((e) => { alert(Object.values(e.response.data.errors).flat()[0] ?? e.response.data?.meta?.message); });
    },

    changeToOpen() {
      if (!confirm("Change cart status to open?")) return;
      this.loaded = false;
      axios
          .patch(`${this.url}/${this.id}/open`)
          .then(() => this.loadData())
          .catch((e) => { alert(Object.values(e.response.data.errors).flat()[0] ?? e.response.data?.meta?.message); });
    },

    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,'0')}`;
    },

    getItemType(item) {
      return item.type === 'experience' ? item.type : `${item.type} (${item.options.gift_format})`;
    },

    getPassengersForHuman(item) {
      const passengers = item.options.dd_passengers ?? item.options.passengers;
      if (passengers === undefined) return '—';
      if (!isNaN(passengers)) return passengers;
      return passengers.map((p) => {
        let price = '';
        if (item.meta.unit_price && typeof item.meta.unit_price === 'object' && p.id in item.meta.unit_price) {
          price = `: ${item.meta.unit_price[p.id] * p.count} AED`;
        }
        return `<span class="passenger-line">${p.count} × ${p.title}${price}</span>`;
      }).join('');
    },

    itemIsNotCopied(index) {
      if (index === 0) return true;
      return JSON.stringify(this.cart.items[index].options) !== JSON.stringify(this.cart.items[index - 1].options);
    },
  },

  computed: {
    getStatusBadge() {
      const map = {
        0: ['status-badge status-badge--green', 'Purchased'],
        1: ['status-badge status-badge--amber', 'Open'],
        2: ['status-badge status-badge--blue',  'In Process'],
      };
      const [cls, label] = map[this.cart.status] ?? ['status-badge status-badge--gray', 'Unknown'];
      return `<span class="${cls}">${label}</span>`;
    },
    isClosed()  { return this.cart.status === 0; },
    isOpen()    { return this.cart.status === 1; },
    isPending() { return this.cart.status === 2; },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }
.btn-back {
  width: 34px; height: 34px; margin-top: 4px;
  background: var(--bg-hover); border: 1px solid var(--border);
  border-radius: 8px; color: var(--text-2); cursor: pointer;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  transition: border-color .15s, color .15s;
}
.btn-back:hover { border-color: var(--accent); color: var(--accent); }
.page-title  { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 6px; }
.page-meta   { display: flex; flex-wrap: wrap; gap: 16px; }
.meta-item   { font-size: 12px; color: var(--text-3); display: flex; align-items: center; gap: 5px; }
.buyer-type-pill {
  background: var(--bg-hover); border: 1px solid var(--border);
  border-radius: 20px; padding: 1px 7px; font-size: 11px; color: var(--text-2);
  text-transform: capitalize;
}

/* ── Info grid ── */
.info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.info-card {
  background: var(--bg-panel); border: 1px solid var(--border);
  border-radius: 12px; padding: 1rem 1.1rem;
  transition: border-color .15s;
}
.info-card--link { cursor: pointer; }
.info-card--link:hover { border-color: var(--accent); }
.info-card__label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; }
.info-card__value { font-size: 14px; font-weight: 500; color: var(--text-1); }
.ref-id { font-size: 11px; color: var(--text-3); margin-top: 3px; }

.link-cell { color: var(--accent); cursor: pointer; font-weight: 500; }
.link-cell:hover { text-decoration: underline; }
.text-muted { color: var(--text-3); }

/* ── Table ── */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-panel__header { padding: .9rem 1.25rem; border-bottom: 1px solid var(--border); }
.table-responsive-wrap { overflow-x: auto; }

.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); white-space: nowrap; text-align: left; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row:last-child td { border-bottom: none; }
.dd-table__row--refunded td { background: rgba(244,47,75,.08); color: var(--text-2); }
.dd-table__row--refunded:hover td { background: rgba(244,47,75,.13); }

.order-id { font-weight: 500; color: var(--text-1); }

.type-badge {
  display: inline-flex; align-items: center; padding: 3px 8px;
  border-radius: 6px; font-size: 11px; font-weight: 500;
  background: var(--bg-hover); border: 1px solid var(--border);
  color: var(--text-2); text-transform: capitalize; white-space: nowrap;
}

.item-title { font-weight: 500; color: var(--text-1); }
.pay-later-badge { font-size: 11px; color: var(--text-3); margin-left: 6px; }

.subtotal { font-weight: 500; color: var(--text-1); white-space: nowrap; }
.gift-status { font-size: 12px; color: var(--text-2); }

/* passenger lines from v-html */
.action-btns { display: flex; gap: 6px; }
.btn-icon {
  width: 30px; height: 30px;
  border-radius: 7px; border: 1px solid var(--border);
  background: var(--bg-hover); color: var(--text-2);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 13px;
  transition: border-color .15s, color .15s, background .15s;
}
.btn-icon--green:hover { border-color: #22c55e; color: #22c55e; background: rgba(34,197,94,.08); }
.btn-icon--red:hover   { border-color: var(--accent); color: var(--accent); background: rgba(244,47,75,.08); }

/* ── Bottom grid ── */
.bottom-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: start; }

/* ── Panel shared ── */
.panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.25rem; }
.panel-title { font-size: 12px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 1.1rem; display: flex; align-items: center; gap: 7px; }
.additional-services { font-size: 12px; color: var(--text-2); overflow-wrap: anywhere; min-width: 180px; }
.additional-services__line + .additional-services__line { margin-top: 3px; }
.additional-services__total { margin-top: 4px; font-weight: 500; color: var(--text-1); }

/* ── Summary ── */
.summary-rows { display: flex; flex-direction: column; gap: 0; }
.summary-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 9px 0; border-bottom: 1px solid var(--border);
  font-size: 13px; color: var(--text-2);
}
.summary-row:last-child { border-bottom: none; }
.summary-row--total { padding-top: 12px; margin-top: 4px; }
.summary-val { font-weight: 500; color: var(--text-1); }
.summary-val--green { color: #22c55e; font-size: 16px; }
.text-accent { color: var(--accent); }

/* ── Actions ── */
.actions-list { display: flex; flex-direction: column; gap: 10px; }
.action-btn {
  height: 42px; width: 100%; border: none; border-radius: 10px;
  font-size: 13px; font-weight: 500; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: opacity .15s, transform .1s;
}
.action-btn:active { transform: scale(.98); }
.action-btn--primary   { background: var(--accent); color: #fff; }
.action-btn--secondary { background: var(--bg-hover); border: 1px solid var(--border); color: var(--text-2); }
.action-btn--secondary:hover { border-color: var(--border-2); color: var(--text-1); }
.action-btn--green { background: rgba(34,197,94,.12); border: 1px solid rgba(34,197,94,.25); color: #22c55e; }
.action-btn--green:hover { background: rgba(34,197,94,.2); }
.action-btn--amber { background: rgba(245,158,11,.12); border: 1px solid rgba(245,158,11,.25); color: #f59e0b; }
.action-btn--amber:hover { background: rgba(245,158,11,.2); }
.action-btn--primary:hover { opacity: .88; }

/* ── Modal ── */
.dd-modal { background: var(--bg-panel); border-radius: 12px; overflow: hidden; }
.dd-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border); }
.dd-modal__title  { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close  { width: 28px; height: 28px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 6px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s; }
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body   { padding: 1.25rem; }
.dd-modal__footer { display: flex; justify-content: flex-end; gap: 8px; padding: .75rem 1.25rem; border-top: 1px solid var(--border); }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; display: block; }
.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.btn-cancel { height: 36px; padding: 0 16px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; }
.btn-submit { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.btn-submit:hover { opacity: .88; }

/* ══════════════════════════════════════
   Responsive
══════════════════════════════════════ */
@media (max-width: 1024px) { .info-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px)  { .bottom-grid { grid-template-columns: 1fr; } }
@media (max-width: 600px)  { .info-grid { grid-template-columns: 1fr 1fr; } }
</style>
